/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import { Loading } from '@carbon/react';
import { JSONTree } from 'react-json-tree';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt();

function Load() {
  return <Loading small withOverlay={false} data-testid="json-loading-icon" />;
}

function ViewableJSONDisplay({ content, jsonName = null }) {
  // Renders markdown only for "description" keys
  const valueRenderer = (raw, value, keyPath) => {
    const path = Array.isArray(keyPath) ? keyPath : [keyPath];
    const isDescription = path[path.length - 1] === 'description';

    if (isDescription && typeof value === 'string') {
      const markdownInput = value;
      const renderedHtml = md.render(markdownInput);
      const safeHtml = DOMPurify.sanitize(renderedHtml);
      return <div style={{ padding: '0.25rem 0' }} className="markdown-description" dangerouslySetInnerHTML={{ __html: safeHtml }} />;
    }

    return value;
  };

  const explorerTheme = {
    base00: '#f4f4f4', // background
    base01: '#f4f4f4',
    base02: '#e0e0e0',
    base03: '#6f6f6f',
    base04: '#525252',
    base05: '#161616',
    base06: '#161616',
    base07: '#161616',
    base08: '#da1e28',
    base09: '#198038', // numbers / booleans (green)
    base0A: '#0f62fe', // strings (green)
    base0B: '#198038', // strings (green)
    base0C: '#0043ce', // strings (green)
    base0D: '#6f42c1', // keys (purple)
    base0E: '#6f42c1', // keys (purple)
    base0F: '#8a3ffc' // keys (purple)
  };

  return (
    <>
      {!content && <Load />}
      {content && (
        <div data-testid="json-data">
          {jsonName && (
            <span data-testid="json-name" style={{ display: 'none' }}>
              {jsonName}
            </span>
          )}
          <JSONTree
            data={content}
            theme={explorerTheme}
            valueRenderer={valueRenderer}
            style={{
              fontFamily: "'IBM Plex Sans Mono', 'Menlo', 'DejaVu Sans Mono', 'Bitstream Vera Sans Mono', Courier",
              fontSize: '14px'
            }}
            hideRoot={false}
            labelRenderer={([label]) => <strong>{label}:</strong>}
            shouldExpandNodeInitially={() => true}
          />
        </div>
      )}
    </>
  );
}

ViewableJSONDisplay.propTypes = {
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.array]), // JSON Content to be displayed
  jsonName: PropTypes.string // Name to be displayed at top of JSON Viewer
};

export default ViewableJSONDisplay;
